import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RoundResult, Trin, UserProfile } from '../types';
import { trinLabel } from '../lib/curriculum';
import { useEnterAction } from '../lib/useEnterAction';
import { useRoundInput } from '../lib/useRoundInput';
import {
  buildTrinProofText,
  isTrinProofPassed,
  TRIN_PROOF_DURATION_MS,
} from '../lib/trinProof';
import Keyboard from './Keyboard';

interface TrinProofScreenProps {
  trin: Trin;
  user: UserProfile;
  onFinish: (result: RoundResult, passed: boolean) => void;
  onCancel: () => void;
}

export default function TrinProofScreen({
  trin,
  user,
  onFinish,
  onCancel,
}: TrinProofScreenProps) {
  const [phase, setPhase] = useState<'intro' | 'playing'>('intro');
  const targetText = useMemo(() => buildTrinProofText(trin), [trin]);
  const finishedRef = useRef(false);

  const handleFinish = useCallback(
    (r: RoundResult) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      onFinish(r, isTrinProofPassed(r.accuracy, r.completed));
    },
    [onFinish],
  );

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
      <header className="flex items-center justify-between">
        <div>
          <p className="label">🎯 Trin-bevis</p>
          <h1 className="mt-1 text-2xl font-semibold text-strong">{trin.title}</h1>
          <p className="text-sm text-muted">{trinLabel(trin)}</p>
        </div>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Afslut
        </button>
      </header>

      {phase === 'intro' ? (
        <TrinProofIntro onStart={() => setPhase('playing')} />
      ) : (
        <TrinProofRound
          trin={trin}
          user={user}
          targetText={targetText}
          onFinish={handleFinish}
        />
      )}
    </div>
  );
}

function TrinProofIntro({ onStart }: { onStart: () => void }) {
  useEnterAction(onStart);
  const secs = Math.round(TRIN_PROOF_DURATION_MS / 1000);
  return (
    <div className="surface mx-auto w-full max-w-xl p-6 text-center animate-fade-in-up ring-2 ring-amber-200 dark:ring-amber-500/30">
      <p className="text-sm text-strong">
        Vis at du kan dette uden at tøve — {secs} sekunder.
      </p>
      <p className="mt-2 text-xs text-muted">
        Bestå med mindst 95% nøjagtighed. WPM tæller ikke med.
      </p>
      <button
        type="button"
        autoFocus
        onClick={onStart}
        className="mt-6 rounded-xl bg-amber-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-amber-400"
      >
        Start trin-bevis
      </button>
    </div>
  );
}

function TrinProofRound({
  trin,
  user,
  targetText,
  onFinish,
}: {
  trin: Trin;
  user: UserProfile;
  targetText: string;
  onFinish: (r: RoundResult) => void;
}) {
  const input = useRoundInput({
    targetText,
    mode: 'challenge',
    roundId: '__trin-proof__',
    roundTitle: 'Trin-bevis',
    onFinish,
  });

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (!input.finished) input.finish(input.cursor >= targetText.length);
    }, TRIN_PROOF_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [input, targetText.length]);

  const remaining = Math.max(0, TRIN_PROOF_DURATION_MS - input.elapsedMs);

  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Tid tilbage" value={formatMs(remaining)} />
        <Stat
          label="Nøjagtighed"
          value={`${(input.liveAcc * 100).toFixed(0)}%`}
        />
        <Stat label="WPM" value={input.liveWpm.toFixed(0)} />
      </div>
      <div className="surface p-6 ring-2 ring-amber-200/80 dark:ring-amber-500/25">
        <p className="whitespace-pre-wrap break-words font-mono text-2xl leading-relaxed tracking-wide">
          {[...targetText].map((ch, i) => {
            const isCorrect = input.correctMask[i];
            const isCursor = i === input.cursor;
            let cls = 'text-zinc-400 dark:text-zinc-500';
            if (isCorrect) cls = 'text-emerald-600 dark:text-emerald-300';
            else if (isCursor)
              cls =
                'text-zinc-900 dark:text-zinc-100 bg-amber-100 dark:bg-amber-500/10 rounded-sm border-l-2 border-amber-500';
            return (
              <span key={i} className={cls}>
                {ch === ' ' ? '\u00A0' : ch}
              </span>
            );
          })}
        </p>
      </div>
      <Keyboard
        layoutId={user.keyboardLayout}
        nextExpectedKey={input.expectedChar}
        lastPressedKey={input.lastPress?.key}
        lastPressCorrect={input.lastPress?.correct}
        unlockedKeys={trin.unlockedKeys}
        hintLevel="none"
        showFingerDot={false}
      />
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface-soft px-4 py-3">
      <p className="label">{label}</p>
      <p className="mt-1 font-mono text-2xl text-strong">{value}</p>
    </div>
  );
}

function formatMs(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
